// packages/core/src/learning/schools/value-investing.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. Worldview named 'benjamin-graham',
// 'warren-buffett', 'charlie-munger', 'michael-burry' (all valid FigureIds) —
// wire into `figures` during the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const VALUE_INVESTING_EXHIBIT: SchoolExhibitEntry = {
	id: 'value-investing',
	tagline: 'Value investing believes price and value are not the same thing.',
	worldviewSummary:
		'Value investing is paying attention to the gap between what something costs and what it may actually be worth. This lens protects you from confusing popularity, price movement, or market excitement with real value. It accepts the tradeoff that being early, lonely, or wrong can feel the same for a long time. The blind spot is overconfidence: a cheap looking asset can stay cheap because the business is weak, the thesis is wrong, or the value is harder to unlock than expected.',
	coreBeliefs: [
		'Value investing believes price and value can separate.',
		'Value investing believes a margin of safety matters because humans make mistakes.',
		'Value investing believes patience is part of the edge, not a personality flex.',
		'Value investing believes unpopular does not always mean broken.',
		'Value investing believes analysis should start with downside, not fantasy upside.',
		'Value investing believes temperament matters because the market can test your conviction before it rewards your judgment.',
	],
	warnsAgainst: [
		'Paying any price for a popular story.',
		'Confusing a falling price with a bargain.',
		'Ignoring business quality because the numbers look cheap.',
		'Mistaking stubbornness for conviction.',
	],
	helps: 'This lens helps people who want to think carefully about price, business quality, patience, and downside risk. It may not fit someone who wants fast feedback, simple passive investing, or a style that avoids deep analysis.',
	figures: [],
	canon: [
		{
			title: 'The Intelligent Investor',
			author: 'Benjamin Graham',
			oneLineIdea:
				'Treat the market as emotional, then use discipline instead of mood.',
		},
		{
			title: 'Security Analysis',
			author: 'Benjamin Graham',
			oneLineIdea:
				'Study assets with a focus on evidence, downside, and what the numbers can support.',
		},
		{
			title: 'Margin of safety',
			oneLineIdea:
				'Leave room for being wrong because the future will not follow your spreadsheet perfectly.',
		},
		{
			title: 'Intrinsic value',
			oneLineIdea:
				'The core question is what a business may be worth, not what people are willing to pay today.',
		},
		{
			title: 'Circle of competence',
			oneLineIdea:
				'A value lens works better when you admit what you do not understand.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-value-investing',
	debateIds: [],
	applyActions: [
		{
			label: 'Compare a money story against your real numbers',
			action: {
				type: 'openDashboard',
				label: 'Check your money picture',
			},
		},
		{
			label: 'Test how patience changes a long term plan',
			action: {
				type: 'openSimulator',
				label: 'Simulate a patient plan',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Write your margin of safety before emotion gets involved',
			action: {
				type: 'noToolAction',
				label: 'Write your safety margin',
				prompt: 'Write what could go wrong with an idea before writing what could go right.',
			},
		},
		{
			label: 'Check whether your runway supports patience',
			action: { type: 'calculateRunway', label: 'Check your runway' },
		},
	],
	marketingHooks: [
		{
			line: 'Value investing starts with one uncomfortable idea: the crowd can be loud and still wrong.',
			kind: 'hook',
		},
		{
			line: 'A cheap price is not a bargain if the business is quietly breaking.',
			kind: 'hook',
		},
		{
			line: 'What is the most expensive mistake people make when they confuse popularity with value?',
			kind: 'interview-question',
		},
		{
			line: 'When did patience help you, and when did it turn into stubbornness?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps users test money ideas through tradeoffs, not market hype.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Value investing teaches one of money’s hardest skills: waiting without lying to yourself.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience the difference between a real bargain and a company that only looks cheap.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask someone to explain what they think something is worth before asking what it costs.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
